import { useState, type FormEvent } from "react";
import { useSearchParams } from "react-router";
import { postJson } from "../api";
import { useAiData, useTitle } from "../ai/parts";
import { MobileActions, RowMenu, RowsSkeleton, Section, useIsMobile, Window } from "../ai/widgets";
import ui from "../ai/ai.module.css";
import { OrgNotFound } from "../pages/StatePages";
import { useOpenOrg } from "../shell/openOrg";
import { formatText, useTexts } from "../texts";
import { ChoiceMenu } from "../ui/ChoiceMenu";
import { ChatIcon } from "../ui/icons/Icon";
import { Notice, PageHead, Switch, TextField, useToast } from "../ui/kit";
import { ListFooter, useServerList } from "../ui/list";
import { PageState } from "../ui/pieces";
import {
  dateIn,
  eventText,
  moduleName,
  modulesByRoleKey,
  normalizeTab,
  peopleListKey,
  plural,
  policyText,
  roleKey,
  userTabs,
  whenIn,
  type PersonStatus,
  type UserTab,
} from "./organizations";
import { usePersonActions, type Person } from "./PersonActions";
import {
  AccessFacts,
  AttemptsList,
  Facts,
  SessionsBlock,
  type OpenSession,
  type SecurityState,
} from "./SecurityBlocks";
import { CellSwitch, SimpleTable } from "./SimpleTable";
import styles from "./orgs.module.css";

export type UserRecord = {
  organization: { id: number; name: string; code: string; timezone: string };
  member: {
    userId: number;
    name: string;
    email: string;
    loginName: string | null;
    status: PersonStatus;
    role: string;
    isOwner: boolean;
    /** Identity: su rol ya le da todos los módulos encendidos (sin conceder uno por uno). */
    modulesByRole?: boolean;
    membershipStatus: string;
    accountStatus: string;
    locked: boolean;
    lastLoginAt: string | null;
  };
  memberSince: string | null;
  passwordChangedAt: string | null;
  recoveryEmail: string | null;
  security: SecurityState | null;
  mfa: { enabled: boolean } | null;
  policy: { passwordExpiration: string | null; mfaMode: string | null };
  sessions: OpenSession[] | null;
  modules: { code: string; name: string; orgEnabled: boolean; granted: boolean }[] | null;
  inboxes: {
    memberships: { inboxId: number; name: string; role: string; status: string }[];
    all: { inboxId: number; name: string }[];
  } | null;
  canImpersonate: boolean;
};

const tabKey: Record<UserTab, string> = {
  resumen: "ADM_PPL_UTAB_SUMMARY",
  acceso: "ADM_PPL_UTAB_ACCESS",
  seguridad: "ADM_PPL_UTAB_SECURITY",
  modulos: "ADM_PPL_UTAB_MODULES",
  bandejas: "ADM_PPL_UTAB_INBOXES",
  actividad: "ADM_PPL_UTAB_ACTIVITY",
};

/** Dirección de ← : la lista de Usuarios con el filtro y la página que tenía (E84). */
function backHref(orgId: number) {
  let saved = "";
  try {
    saved = sessionStorage.getItem(peopleListKey(orgId)) ?? "";
  } catch {
    saved = "";
  }
  const params = new URLSearchParams(saved);
  params.set("id", String(orgId));
  return `/Organizations/Usuarios?${params.toString()}`;
}

/**
 * `emp-usuario` · Personas › ← persona (§5.4, nivel 4): el mismo encabezado en las seis pestañas (nombre, rol, correo,
 * usuario, último acceso; «Abrir como usuario» si se puede y ⋮ con Restablecer, Cerrar sesiones, Bloquear y Desactivar),
 * las pestañas en la dirección (`tab=`) y una sola lectura para el encabezado y las pestañas. Reparte las funciones de
 * Usuario.razor sin perder ninguna: Desbloquear, intentos y sesiones viven solo en Seguridad (H-5).
 */
export function UserPage({ orgId, userId }: { orgId: number; userId: number }) {
  const { L, locale } = useTexts();
  const toast = useToast();
  const mobile = useIsMobile();
  const [params] = useSearchParams();
  const tab = normalizeTab(params.get("tab"));
  const data = useAiData<UserRecord>(`/api/admin/organizations/${orgId}/members/${userId}`);
  const record = data.data;
  useOpenOrg(record?.organization ?? null);
  useTitle(record?.member.name ?? L("ADM_ORG_PEOPLE"));
  const reload = () => void data.reload();
  const actions = usePersonActions(orgId, record?.organization.name ?? "", reload);
  const [assign, setAssign] = useState(false);
  const back = { href: backHref(orgId), label: L("ADM_ORG_PEOPLE") };

  if (data.state === "missing") return <OrgNotFound />;
  if (data.state === "failed" && !record)
    return (
      <PageState
        head={<PageHead title={L("ADM_ORG_PEOPLE")} back={back} />}
        title={data.error?.status === 403 ? L("ADM_FORBIDDEN") : L("ADM_UNAVAILABLE")}
        line={data.error?.status === 403 ? L("ADM_FORBIDDEN_LINE") : L("ADM_UNAVAILABLE_LINE")}
        action={
          data.error?.status === 403 ? undefined : (
            <button type="button" className="tm-btn secondary" onClick={data.retry}>
              {L("ADM_RETRY")}
            </button>
          )
        }
      />
    );

  const tabs = (
    <nav className={`tm-tabs ${ui.tabs}`} aria-label={L("ADM_PPL_UTABS")} data-part="pestanas">
      {userTabs.map((value) => (
        <a
          key={value}
          href={`/Organizations/Usuario?id=${orgId}&userId=${userId}&tab=${value}`}
          aria-current={value === tab ? "page" : undefined}
        >
          {L(tabKey[value])}
        </a>
      ))}
    </nav>
  );
  if (!record)
    return (
      <>
        <PageHead title={L("ADM_ORG_PEOPLE")} back={back} />
        {tabs}
        <RowsSkeleton rows={3} table />
      </>
    );

  const member = record.member;
  const zone = record.organization.timezone;
  const person: Person = {
    userId: member.userId,
    name: member.name,
    status: member.status,
    isOwner: member.isOwner,
    sessions: record.sessions?.length ?? null,
  };
  const menuItems = [
    ...(mobile && record.canImpersonate
      ? [{ label: L("ADM_PPL_OPEN_AS_MENU"), onSelect: () => actions.openImpersonate(person) }]
      : []),
    ...actions.items(person, { withBlock: true, withUnlock: false }),
  ];
  const sub = [
    member.email,
    member.loginName ? formatText(L("ADM_PPL_USERNAME"), member.loginName) : null,
    formatText(
      L("ADM_PPL_LAST_ACCESS_INLINE"),
      member.lastLoginAt ? whenIn(L, locale, member.lastLoginAt, zone, true) : L("ADM_PPL_NEVER_LOWER"),
    ),
  ]
    .filter(Boolean)
    .join(" · ");
  const primary =
    tab === "acceso" ? (
      <button type="submit" form="user-access" className="tm-btn primary">
        {L("ADM_PPL_SAVE")}
      </button>
    ) : tab === "bandejas" && record.inboxes ? (
      <button type="button" className="tm-btn primary" onClick={() => setAssign(true)}>
        {L("ADM_PPL_ASSIGN_INBOX")}
      </button>
    ) : null;

  return (
    <>
      <PageHead
        title={member.name}
        back={back}
        titleTag={<span className="tm-tag">{L(roleKey(member.role))}</span>}
        sub={sub}
      >
        <RowMenu label={L("ADM_MORE_ACTIONS")} title={member.name} items={menuItems} />
        {record.canImpersonate ? (
          <button
            type="button"
            className={`tm-btn secondary ${ui.desktopInline}`}
            onClick={() => actions.openImpersonate(person)}
          >
            {L("ADM_PPL_OPEN_AS")}
          </button>
        ) : null}
        {primary ? <span className={ui.desktopInline}>{primary}</span> : null}
      </PageHead>
      {tabs}
      {tab === "resumen" ? <SummaryTab record={record} /> : null}
      {tab === "acceso" ? (
        <AccessTab
          key={`${member.role}|${member.membershipStatus}|${record.mfa?.enabled}|${member.loginName}|${record.recoveryEmail}`}
          orgId={orgId}
          record={record}
          onSaved={() => {
            reload();
            toast.show(formatText(L("ADM_PPL_ACCESS_SAVED"), member.name));
          }}
        />
      ) : null}
      {tab === "seguridad" ? (
        <SecurityTab orgId={orgId} record={record} person={person} actions={actions} onChanged={reload} />
      ) : null}
      {tab === "modulos" ? <ModulesTab orgId={orgId} record={record} onChanged={reload} /> : null}
      {tab === "bandejas" ? <InboxesTab orgId={orgId} record={record} onChanged={reload} /> : null}
      {tab === "actividad" ? <ActivityTab orgId={orgId} userId={userId} zone={zone} /> : null}
      {primary ? <MobileActions>{primary}</MobileActions> : null}
      {actions.windows}
      {record.inboxes ? (
        <AssignInboxWindow
          orgId={orgId}
          record={record}
          open={assign}
          onClose={() => setAssign(false)}
          onDone={reload}
        />
      ) : null}
    </>
  );
}

function membershipText(L: (key: string) => string, status: string) {
  return status === "ACTIVE" ? L("ADM_PPL_ACTIVE_F") : L("ADM_PPL_INACTIVE_F");
}

function accountText(L: (key: string) => string, record: UserRecord) {
  if (record.member.locked) return L("ADM_PPL_BLOCKED_F");
  return record.member.accountStatus === "ACTIVE" ? L("ADM_PPL_ACTIVE_F") : L("ADM_PPL_INACTIVE_F");
}

/** Resumen: solo lectura (rol, membresía, cuenta, último acceso, último cambio de contraseña, verificación). */
function SummaryTab({ record }: { record: UserRecord }) {
  const { L, locale } = useTexts();
  const zone = record.organization.timezone;
  return (
    <Facts
      items={[
        { label: L("ADM_PPL_ROLE"), value: L(roleKey(record.member.role)) },
        { label: L("ADM_PPL_MEMBERSHIP"), value: membershipText(L, record.member.membershipStatus) },
        { label: L("ADM_PPL_ACCOUNT"), value: accountText(L, record) },
        {
          label: L("ADM_PPL_LAST_ACCESS"),
          value: record.member.lastLoginAt ? whenIn(L, locale, record.member.lastLoginAt, zone) : L("ADM_PPL_NEVER"),
        },
        {
          label: L("ADM_PPL_PASSWORD_CHANGED"),
          value: record.passwordChangedAt ? dateIn(locale, record.passwordChangedAt, zone) : "—",
        },
        {
          label: L("ADM_PPL_MFA"),
          value: !record.mfa ? "—" : record.mfa.enabled ? L("ADM_PPL_MFA_ON_EMAIL") : L("ADM_PPL_MFA_OFF"),
        },
      ]}
    />
  );
}

/**
 * Acceso: rol (el propietario no se cambia aquí), membresía, verificación en dos pasos, la seguridad de la empresa como
 * dato (se cambia en Políticas), nombre de acceso y correo de recuperación. Guardar envía todo lo que se ve; el servidor
 * aplica solo lo que cambió (E105). Si falla, el error en su campo o arriba, sin perder lo escrito.
 */
function AccessTab({ orgId, record, onSaved }: { orgId: number; record: UserRecord; onSaved: () => void }) {
  const { L } = useTexts();
  const member = record.member;
  const [role, setRole] = useState(member.role === "ORG_MEMBER" ? "ORG_MEMBER" : "ORG_ADMIN");
  const [active, setActive] = useState(member.membershipStatus === "ACTIVE");
  const [mfa, setMfa] = useState(record.mfa?.enabled ?? false);
  const [login, setLogin] = useState(member.loginName ?? "");
  const [recovery, setRecovery] = useState(record.recoveryEmail ?? "");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [general, setGeneral] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    const found: Record<string, string> = {};
    if (login.trim().length > 50) found.loginName = L("ADD_ERR_DATOS");
    if (recovery.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recovery.trim()))
      found.recoveryEmail = L("ORGCORE_EMAIL_REQUIRED");
    setErrors(found);
    setGeneral(null);
    if (Object.keys(found).length) return;
    setBusy(true);
    const reply = await postJson<{ saved?: boolean; field?: string; messageKey?: string }>(
      `/api/admin/organizations/${orgId}/members/${member.userId}/access`,
      { role, membershipActive: active, mfaEnabled: mfa, loginName: login.trim(), recoveryEmail: recovery.trim() },
    );
    setBusy(false);
    if (reply.status === 200 && reply.body?.saved) return onSaved();
    const message = L(reply.body?.messageKey ?? "ADMIN_ERR_GENERIC");
    if (reply.body?.field) setErrors({ [reply.body.field]: message });
    else setGeneral(message);
  };
  const ownerLink = (
    <a className={styles.helpLink} href={`/Organizations/Propietario?id=${orgId}`}>
      {L("ADM_PPL_OWNER_PATH")}
    </a>
  );
  return (
    <form id="user-access" className={`tm-form ${styles.accessGrid}`} noValidate onSubmit={(e) => void submit(e)}>
      {general ? (
        <div className={styles.full}>
          <Notice tone="error">{general}</Notice>
        </div>
      ) : null}
      <div className={styles.field}>
        <span className={styles.fieldTitle} id="access-role">
          {L("ADM_PPL_ROLE")}
        </span>
        {member.isOwner ? (
          <strong>{L("ADM_PPL_ROLE_OWNER")}</strong>
        ) : (
          <div className={styles.choice} role="radiogroup" aria-labelledby="access-role">
            {(["ORG_ADMIN", "ORG_MEMBER"] as const).map((value) => (
              <button
                key={value}
                type="button"
                role="radio"
                className="tm-pill"
                aria-checked={role === value ? "true" : "false"}
                aria-pressed={role === value ? "true" : "false"}
                onClick={() => setRole(value)}
              >
                {L(roleKey(value))}
              </button>
            ))}
          </div>
        )}
        <small className={styles.help}>
          {L("ADM_PPL_OWNER_HELP")} {ownerLink}.
        </small>
      </div>
      <div className={styles.field}>
        <span className={styles.fieldTitle}>{L("ADM_PPL_MEMBERSHIP")}</span>
        {member.isOwner ? (
          <span>{membershipText(L, member.membershipStatus)}</span>
        ) : (
          <Switch
            title={L("ADM_PPL_MEMBERSHIP_SWITCH")}
            line={L(active ? "ADM_PPL_MEMBERSHIP_ON" : "ADM_PPL_MEMBERSHIP_OFF")}
            checked={active}
            onChange={setActive}
          />
        )}
      </div>
      <div className={styles.field}>
        <span className={styles.fieldTitle}>{L("ADM_PPL_MFA")}</span>
        {record.mfa ? (
          <Switch
            title={L("ADM_PPL_MFA_SWITCH")}
            line={L(mfa ? "ADM_PPL_MFA_SWITCH_ON" : "ADM_PPL_MFA_SWITCH_OFF")}
            checked={mfa}
            onChange={setMfa}
          />
        ) : (
          <span className={ui.muted}>{L("ADM_ORGS_NOT_AVAILABLE")}</span>
        )}
      </div>
      <div className={styles.field}>
        <span className={styles.fieldTitle}>{L("ADM_PPL_ORG_SECURITY")}</span>
        <div className={styles.readonly}>
          <div className={styles.readonlyBox} data-part="dato-solo-lectura">
            <strong>{policyText(L, record.policy.passwordExpiration, record.policy.mfaMode)}</strong>
            <small>{L("ADM_PPL_ORG_SECURITY_LINE")}</small>
          </div>
          <a className="tm-btn secondary" href={`/Organizations/Politicas?id=${orgId}`}>
            {L("ADM_PPL_CHANGE_IN_POLICIES")}
          </a>
        </div>
      </div>
      <TextField
        id="access-login"
        field="loginName"
        label={L("ADM_PPL_LOGIN_ALT")}
        optional={L("CFG1_R1_OPTIONAL")}
        help={L("ADM_PPL_LOGIN_ALT_HELP")}
        value={login}
        maxLength={50}
        error={errors.loginName}
        onChange={(value) => (setLogin(value), value.length <= 50 && setErrors((now) => ({ ...now, loginName: "" })))}
      />
      <TextField
        id="access-recovery"
        field="recoveryEmail"
        type="email"
        label={L("ADM_PPL_RECOVERY_EMAIL")}
        optional={L("CFG1_R1_OPTIONAL")}
        value={recovery}
        maxLength={200}
        placeholder={L("ADM_PPL_RECOVERY_EXAMPLE")}
        error={errors.recoveryEmail}
        onChange={(value) => (setRecovery(value), setErrors((now) => ({ ...now, recoveryEmail: "" })))}
      />
    </form>
  );
}

/**
 * Seguridad: contraseña (Restablecer), estado de acceso (Bloquear o Desbloquear con «Deshacer») con sus datos, y las
 * sesiones abiertas (Cerrar una o Cerrar todas, las dos confirman).
 */
function SecurityTab({
  orgId,
  record,
  person,
  actions,
  onChanged,
}: {
  orgId: number;
  record: UserRecord;
  person: Person;
  actions: ReturnType<typeof usePersonActions>;
  onChanged: () => void;
}) {
  const { L, locale } = useTexts();
  const zone = record.organization.timezone;
  const security = record.security;
  const sessions = record.sessions;
  return (
    <>
      <Section
        id="sec-password"
        title={L("ADM_PPL_PASSWORD")}
        line={formatText(
          L("ADM_PPL_PASSWORD_LAST"),
          record.passwordChangedAt ? dateIn(locale, record.passwordChangedAt, zone) : "—",
        )}
        action={
          <button type="button" className="tm-btn secondary" onClick={() => actions.openReset(person)}>
            {L("ADM_PPL_RESET")}
          </button>
        }
      >
        {null}
      </Section>
      <Section
        id="sec-state"
        title={L("ADM_PPL_ACCESS_STATE")}
        action={
          person.status === "blocked" ? (
            <button type="button" className="tm-btn secondary" onClick={() => void actions.unlock(person)}>
              {L("ADM_PPL_UNLOCK")}
            </button>
          ) : (
            <button type="button" className="tm-btn secondary" onClick={() => actions.openBlock(person)}>
              {L("ADM_PPL_BLOCK")}
            </button>
          )
        }
      >
        {security ? (
          <AccessFacts security={security} zone={zone} />
        ) : (
          <Notice tone="warning">{L("ADM_PPL_SECURITY_UNAVAILABLE")}</Notice>
        )}
      </Section>
      <Section
        id="sec-sessions"
        title={L("ADM_PPL_SESSIONS")}
        line={sessions ? plural(L, locale, sessions.length, "ADM_PPL_N_SESSIONS_ONE", "ADM_PPL_N_SESSIONS") : undefined}
        action={
          sessions && sessions.length > 0 ? (
            <button type="button" className="tm-btn secondary" onClick={() => actions.openRevoke(person)}>
              {L("ADM_PPL_CLOSE_ALL")}
            </button>
          ) : null
        }
      >
        <SessionsBlock
          orgId={orgId}
          userId={record.member.userId}
          name={record.member.name}
          sessions={sessions}
          zone={zone}
          onChanged={onChanged}
        />
      </Section>
    </>
  );
}

/**
 * Módulos: solo los de la empresa; uno apagado en la empresa sin interruptor (C41). Conceder o quitar con «Deshacer».
 * Propietario y Administrador ya entran a todos por su rol (lo dice Identity): «Por su rol» en lugar del interruptor,
 * que no decidiría nada (C38, valores que no aplican), y el motivo una vez arriba.
 */
function ModulesTab({ orgId, record, onChanged }: { orgId: number; record: UserRecord; onChanged: () => void }) {
  const { L } = useTexts();
  const toast = useToast();
  const [busy, setBusy] = useState<string | null>(null);
  const modules = record.modules;
  const name = record.member.name;
  const byRole = record.member.modulesByRole === true;
  const line = byRole ? L(modulesByRoleKey(record.member.role)) : L("ADM_PPL_MODULES_LINE");
  const change = async (code: string, grant: boolean) => {
    setBusy(code);
    const reply = await postJson<{ saved?: boolean; messageKey?: string }>(
      `/api/admin/organizations/${orgId}/members/${record.member.userId}/modules/${encodeURIComponent(code)}/${grant ? "grant" : "revoke"}`,
      {},
    );
    setBusy(null);
    if (reply.status !== 200 || !reply.body?.saved) {
      toast.show(L(reply.body?.messageKey ?? "ADMIN_ERR_GENERIC"));
      return false;
    }
    onChanged();
    return true;
  };
  const toggle = async (module: { code: string; name: string }, grant: boolean) => {
    if (!(await change(module.code, grant))) return;
    const label = moduleName(L, module.code, { [module.code]: module.name });
    toast.show(formatText(L(grant ? "ADM_PPL_MODULE_GRANTED" : "ADM_PPL_MODULE_REVOKED"), label, name), {
      label: L("ADM_UNDO"),
      run: () => void change(module.code, !grant),
    });
  };
  if (!modules) return <Notice tone="warning">{L("ADM_PPL_MODULES_UNAVAILABLE")}</Notice>;
  const control = (module: { code: string; name: string; orgEnabled: boolean; granted: boolean }) =>
    module.orgEnabled && byRole ? (
      <span className={styles.notApplicable} title={line}>
        {L("ADM_PPL_BY_ROLE")}
      </span>
    ) : module.orgEnabled ? (
      <CellSwitch
        className={styles.switchCell}
        trackClass={styles.track}
        checked={module.granted}
        label={moduleName(L, module.code, { [module.code]: module.name })}
        text={L(module.granted ? "ADM_PPL_GRANTED" : "ADM_PPL_NOT_GRANTED")}
        disabled={busy === module.code}
        onChange={(value) => void toggle(module, value)}
      />
    ) : (
      <span className={styles.notApplicable}>{L("ADM_PPL_OFF_IN_ORG")}</span>
    );
  return (
    <>
      <p className={ui.note}>{line}</p>
      <SimpleTable
        label={L("ADM_PPL_UTAB_MODULES")}
        rows={modules}
        rowKey={(module) => module.code}
        className={styles.settings}
        columns={[
          {
            key: "module",
            title: L("ADM_ORGS_COL_MODULE"),
            render: (module) => <strong>{moduleName(L, module.code, { [module.code]: module.name })}</strong>,
          },
          { key: "access", title: L("ADM_PPL_COL_ACCESS"), render: control },
        ]}
        mobile={(module) => ({
          title: moduleName(L, module.code, { [module.code]: module.name }),
          end: control(module),
        })}
      />
    </>
  );
}

/** Bandejas: la función en la celda y la pausa con «Deshacer»; «Asignar a una bandeja» abre su ventana. */
function InboxesTab({ orgId, record, onChanged }: { orgId: number; record: UserRecord; onChanged: () => void }) {
  const { L } = useTexts();
  const toast = useToast();
  const name = record.member.name;
  const inboxes = record.inboxes;
  const base = `/api/admin/organizations/${orgId}/members/${record.member.userId}/inboxes`;
  const send = async (url: string, body: unknown) => {
    const reply = await postJson<{ saved?: boolean; messageKey?: string }>(url, body);
    if (reply.status !== 200 || !reply.body?.saved) {
      toast.show(L(reply.body?.messageKey ?? "ADMIN_ERR_GENERIC"));
      return false;
    }
    onChanged();
    return true;
  };
  if (!inboxes) return <Notice tone="warning">{L("ADM_PPL_INBOXES_UNAVAILABLE")}</Notice>;
  if (inboxes.memberships.length === 0)
    return (
      <div className="tm-empty" role="status" data-part="vacio">
        <strong>{L("ADM_PPL_NO_INBOXES")}</strong>
        <span>{L("ADM_PPL_NO_INBOXES_LINE")}</span>
      </div>
    );
  const setRole = async (item: { inboxId: number; name: string; role: string }, role: string) => {
    if (role === item.role) return;
    if (!(await send(`${base}/${item.inboxId}/role`, { role }))) return;
    toast.show(formatText(L(role === "SUPERVISOR" ? "ADM_PPL_NOW_SUPERVISOR" : "ADM_PPL_NOW_AGENT"), name, item.name), {
      label: L("ADM_UNDO"),
      run: () => void send(`${base}/${item.inboxId}/role`, { role: item.role }),
    });
  };
  const setStatus = async (item: { inboxId: number; name: string; status: string }, active: boolean) => {
    const status = active ? "ACTIVE" : "INACTIVE";
    if (!(await send(`${base}/${item.inboxId}/status`, { status }))) return;
    toast.show(formatText(L(active ? "ADM_PPL_INBOX_RESUMED" : "ADM_PPL_INBOX_PAUSED"), name, item.name), {
      label: L("ADM_UNDO"),
      run: () => void send(`${base}/${item.inboxId}/status`, { status: item.status }),
    });
  };
  const roleMenu = (item: { inboxId: number; name: string; role: string }) => (
    <ChoiceMenu
      id={`inbox-role-${item.inboxId}`}
      label={formatText(L("ADM_PPL_ROLE_IN"), item.name)}
      value={item.role === "SUPERVISOR" ? "SUPERVISOR" : "AGENT"}
      options={[
        { value: "AGENT", label: L("ROLE_AGENT") },
        { value: "SUPERVISOR", label: L("ROLE_SUPERVISOR") },
      ]}
      onChange={(value) => void setRole(item, value)}
      chevron={<ChatIcon name="chevron-down" />}
      check={<ChatIcon name="check" />}
    />
  );
  const statusSwitch = (item: { inboxId: number; name: string; status: string }) => (
    <CellSwitch
      className={styles.switchCell}
      trackClass={styles.track}
      checked={item.status === "ACTIVE"}
      label={item.name}
      text={L(item.status === "ACTIVE" ? "ADM_PPL_INBOX_ACTIVE" : "ADM_PPL_INBOX_PAUSED_STATE")}
      onChange={(value) => void setStatus(item, value)}
    />
  );
  return (
    <SimpleTable
      label={L("ADM_PPL_UTAB_INBOXES")}
      rows={inboxes.memberships}
      rowKey={(item) => item.inboxId}
      columns={[
        { key: "inbox", title: L("ADM_PPL_COL_INBOX"), render: (item) => <strong>{item.name}</strong> },
        { key: "role", title: L("ADM_PPL_COL_FUNCTION"), className: styles.cellControl, render: roleMenu },
        { key: "status", title: L("ADM_PPL_COL_STATUS"), className: styles.statusCol, render: statusSwitch },
      ]}
      mobile={(item) => ({ title: item.name, line: roleMenu(item), end: statusSwitch(item) })}
    />
  );
}

/** `asignar-bandeja` · la bandeja (las activas de la empresa donde no está) y la función. */
function AssignInboxWindow({
  orgId,
  record,
  open,
  onClose,
  onDone,
}: {
  orgId: number;
  record: UserRecord;
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const { L } = useTexts();
  const toast = useToast();
  const [inbox, setInbox] = useState("");
  const [role, setRole] = useState("AGENT");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [wasOpen, setWasOpen] = useState(false);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setInbox("");
      setRole("AGENT");
      setError(null);
    }
  }
  const taken = new Set(
    record.inboxes?.memberships.filter((item) => item.status === "ACTIVE").map((item) => item.inboxId),
  );
  const options = (record.inboxes?.all ?? []).filter((item) => !taken.has(item.inboxId));
  const submit = async () => {
    if (!inbox) return setError(L("ADM_PPL_INBOX_REQUIRED"));
    setBusy(true);
    const reply = await postJson<{ saved?: boolean; messageKey?: string }>(
      `/api/admin/organizations/${orgId}/members/${record.member.userId}/inboxes`,
      { inboxId: Number(inbox), role },
    );
    setBusy(false);
    if (reply.status === 200 && reply.body?.saved) {
      onClose();
      onDone();
      toast.show(
        formatText(
          L("ADM_PPL_ASSIGNED"),
          record.member.name,
          options.find((item) => String(item.inboxId) === inbox)?.name ?? "",
        ),
      );
      return;
    }
    setError(L(reply.body?.messageKey ?? "ADMIN_ERR_GENERIC"));
  };
  return (
    <Window
      open={open}
      title={L("ADM_PPL_ASSIGN_INBOX")}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="tm-btn secondary" onClick={onClose} disabled={busy}>
            {L("ACTION_CANCEL")}
          </button>
          <button
            type="button"
            className="tm-btn primary"
            disabled={busy || options.length === 0}
            aria-busy={busy || undefined}
            onClick={() => void submit()}
          >
            {L("ADM_PPL_ASSIGN")}
          </button>
        </>
      }
    >
      <div className={ui.windowBlock}>
        {options.length === 0 ? (
          <Notice tone="info">{L("ADM_PPL_ALL_INBOXES")}</Notice>
        ) : (
          <>
            <div className={`tm-field${error ? " invalid" : ""}`}>
              <label htmlFor="assign-inbox">{L("ADM_PPL_INBOX")}</label>
              <ChoiceMenu
                id="assign-inbox"
                label={L("ADM_PPL_INBOX")}
                value={inbox}
                placeholder={L("ADM_PPL_CHOOSE_INBOX")}
                options={options.map((item) => ({ value: String(item.inboxId), label: item.name }))}
                onChange={(value) => (setInbox(value), setError(null))}
                invalid={!!error}
                searchLabel={L("CFG1_SEARCH")}
                clearSearchLabel={L("CFG1_R1_CLEAR_SEARCH")}
                searchIcon={<ChatIcon name="search" />}
                clearIcon={<ChatIcon name="x" />}
                chevron={<ChatIcon name="chevron-down" />}
                check={<ChatIcon name="check" />}
              />
              {error ? <span className="tm-field-error">{error}</span> : null}
            </div>
            <span className={styles.fieldTitle} id="assign-role">
              {L("ADM_PPL_FUNCTION")}
            </span>
            <div className={styles.choice} role="radiogroup" aria-labelledby="assign-role">
              {(["AGENT", "SUPERVISOR"] as const).map((value) => (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  className="tm-pill"
                  aria-checked={role === value ? "true" : "false"}
                  aria-pressed={role === value ? "true" : "false"}
                  onClick={() => setRole(value)}
                >
                  {L(value === "AGENT" ? "ROLE_AGENT" : "ROLE_SUPERVISOR")}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </Window>
  );
}

type SecurityEvent = {
  eventId: number;
  occurredAt: string;
  action: string;
  actor: string | null;
  actorContext: string;
  reason: string | null;
};

/** Actividad: intentos de ingreso e historial de seguridad, cada uno con su paginado (D7: sin el JSON del evento). */
function ActivityTab({ orgId, userId, zone }: { orgId: number; userId: number; zone: string }) {
  const { L, locale } = useTexts();
  const events = useServerList<SecurityEvent>(
    `/api/admin/organizations/${orgId}/members/${userId}/security-events`,
    "hpage",
  );
  const failure = (list: { reload: () => void }) => (
    <Notice
      tone="error"
      action={
        <button type="button" className="tm-btn secondary" onClick={list.reload}>
          {L("ADM_RETRY")}
        </button>
      }
    >
      {L("ADM_UNAVAILABLE_LINE")}
    </Notice>
  );
  const who = (event: SecurityEvent) =>
    event.actor
      ? event.actorContext === "PLATFORM_ADMIN"
        ? formatText(L("ADM_PPL_ACTOR_PLATFORM"), event.actor)
        : event.actor
      : L("ADM_PPL_ACTOR_SYSTEM");
  return (
    <>
      <Section id="act-attempts" title={L("ADM_PPL_ATTEMPTS")}>
        <AttemptsList orgId={orgId} userId={userId} zone={zone} />
      </Section>
      <Section id="act-history" title={L("ADM_PPL_HISTORY")}>
        {events.state === "loading" ? (
          <RowsSkeleton rows={3} table />
        ) : events.state !== "ready" ? (
          failure(events)
        ) : events.items.length === 0 ? (
          <div className="tm-empty" role="status">
            <strong>{L("ADM_PPL_NO_HISTORY")}</strong>
          </div>
        ) : (
          <>
            <SimpleTable
              label={L("ADM_PPL_HISTORY")}
              rows={events.items}
              rowKey={(item) => item.eventId}
              columns={[
                {
                  key: "date",
                  title: L("ADM_PPL_COL_DATE"),
                  render: (item) => whenIn(L, locale, item.occurredAt, zone),
                },
                { key: "what", title: L("ADM_PPL_COL_WHAT"), render: (item) => eventText(L, item.action) },
                { key: "who", title: L("ADM_PPL_COL_WHO"), render: who },
                { key: "result", title: L("ADM_PPL_COL_RESULT"), render: () => L("ADM_PPL_ATTEMPT_OK") },
              ]}
              mobile={(item) => ({
                title: eventText(L, item.action),
                line: `${whenIn(L, locale, item.occurredAt, zone)} · ${who(item)}`,
              })}
            />
            <ListFooter list={events} />
          </>
        )}
      </Section>
    </>
  );
}
